import React, { FC, useContext, useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { InfoMessage } from '@eika/attention-message';
import { IconTypes } from '@eika/icon';
import { Input } from '@eika/input';

import { AppContext } from '../../containers/appContextContainer';
import {
    AdresseWeb,
    Postnummer,
    SivilstandWeb,
    SoeknadstypeWeb,
    StatusWeb,
    UtenlandskAdresseWeb,
    ValgtWeb,
} from '../../generated-types';
import { useKontaktinformasjon, useUpdateKontaktinformasjon } from '../../hooks/useLaantaker';
import gyldigEpostFormat from '../../infra/format';
import { useTranslation } from '../../translations';
import MedlaantakerKontaktinfo from '../medlaantaker/medlaantakerKontaktinfo';
import MedlaantakerNew from '../medlaantaker/medlaantakerNew';
import MedlaantakerValg from '../medlaantaker/medlaantakerValg';
import PageCard from '../page/pageCard';
import PageFooter from '../page/pageFooter';

export type KontaktinformasjonFormData = {
    harMedlaantaker: ValgtWeb;
    medlaantakerValid: boolean;
    hovedlaantaker: {
        tlf: string;
        epost: string;
    };
    medlaantaker: {
        fornavn: string;
        etternavn: string;
        personnummer: string;
        tlf: string;
        epost: string;
        sivilstand: SivilstandWeb;
        adresse: AdresseWeb | null;
        postnr: Postnummer | null;
        utenlandskAdresse: UtenlandskAdresseWeb | null;
        harVergemaal: boolean | null;
        statsborgerskap: string | null;
        erKundeIBank: boolean;
        kundeforholdAntallMnd: number;
    };
};

interface Props {
    navigateTo: string;
    navigateFrom: string;
    navigateFromOption?: string;
}

const Kontaktinformasjon: FC<Props> = ({ navigateTo, navigateFrom, navigateFromOption = null }) => {
    const navigate = useNavigate();
    const { soeknadstype } = useContext(AppContext);
    const { kontaktinfoHovedlaantaker, kontaktinfoMedlaantaker, validationMessage } = useTranslation();
    const { data: kontaktinformasjon } = useKontaktinformasjon(soeknadstype);
    const methods = useForm<KontaktinformasjonFormData>({
        defaultValues: {
            harMedlaantaker: ValgtWeb.IKKE_VALGT,
            medlaantakerValid: true,
            medlaantaker: { sivilstand: SivilstandWeb.UOPPGITT },
        },
    });
    const {
        register,
        control,
        reset,
        handleSubmit,
        formState: { errors, isDirty },
    } = methods;
    const hovedlaantaker = kontaktinformasjon?.laantakere.find((l) => l.erHovedlaantaker);
    const medlaantaker = kontaktinformasjon?.laantakere.find((l) => !l.erHovedlaantaker);
    const { mutate, isPending } = useUpdateKontaktinformasjon(kontaktinformasjon, () => {
        navigate(navigateTo);
    });
    const visMedlaantaker = useWatch({ name: 'harMedlaantaker', control });

    useEffect(() => {
        if (kontaktinformasjon) {
            reset({
                harMedlaantaker: kontaktinformasjon.harMedlaantaker,
                medlaantakerValid: true,
                hovedlaantaker: {
                    epost: hovedlaantaker?.epost?.value || undefined,
                    tlf: hovedlaantaker?.tlf?.value || undefined,
                },
                medlaantaker: {
                    fornavn: medlaantaker?.fornavn,
                    etternavn: medlaantaker?.etternavn,
                    personnummer: medlaantaker?.fnr?.value,
                    epost: medlaantaker?.epost?.value || undefined,
                    tlf: medlaantaker?.tlf?.value || undefined,
                    sivilstand: medlaantaker?.sivilstand || undefined,
                    adresse: medlaantaker?.adresse || null,
                },
            });
        }
    }, [kontaktinformasjon, hovedlaantaker, medlaantaker, reset]);

    const onForrigeClick = () => {
        if (navigateFromOption && (soeknadstype === SoeknadstypeWeb.NYTT_BOLIGLAAN || kontaktinformasjon?.annenBolig)) {
            navigate(navigateFromOption);
        } else {
            navigate(navigateFrom);
        }
    };

    const onSubmitKontaktinformasjon = (data: KontaktinformasjonFormData) => {
        if (kontaktinformasjon && isDirty) {
            kontaktinformasjon.laantakere.forEach((l) => {
                if (l.erHovedlaantaker) {
                    l.tlf = { value: data.hovedlaantaker.tlf };
                    l.epost = { value: data.hovedlaantaker.epost };
                } else {
                    l.tlf = { value: data.medlaantaker.tlf };
                    l.epost = { value: data.medlaantaker.epost };
                    l.status = StatusWeb.CONFIRMED;
                    l.adresse = data.medlaantaker.adresse;
                    l.erKundeIBank = data.medlaantaker.erKundeIBank;
                    l.kundeforholdAntallMnd = data.medlaantaker.kundeforholdAntallMnd;
                }
            });
            if (kontaktinformasjon.laantakere.length === 1 && data.harMedlaantaker === ValgtWeb.JA) {
                kontaktinformasjon.laantakere.push({
                    alder: NaN,
                    fornavn: data.medlaantaker.fornavn,
                    etternavn: data.medlaantaker.etternavn,
                    fnr: { value: data.medlaantaker.personnummer },
                    epost: { value: data.medlaantaker.epost },
                    tlf: { value: data.medlaantaker.tlf },
                    notat: null,
                    erHovedlaantaker: false,
                    sivilstand: data.medlaantaker.sivilstand,
                    status: StatusWeb.NEW,
                    inntektWeb: [],
                    loggedIn: false,
                    inntektOgSblInnhentet: false,
                    erKunde: false,
                    adresse: data.medlaantaker.adresse,
                    postnr: data.medlaantaker.postnr,
                    utenlandskAdresse: data.medlaantaker.utenlandskAdresse,
                    harVergemaal: data.medlaantaker.harVergemaal,
                    statsborgerskap: data.medlaantaker.statsborgerskap,
                    erKundeIBank: data.medlaantaker.erKundeIBank,
                    kundeforholdAntallMnd: data.medlaantaker.kundeforholdAntallMnd,
                });
            }
            kontaktinformasjon.harMedlaantaker = data.harMedlaantaker;
            mutate(kontaktinformasjon);
        } else {
            navigate(navigateTo);
        }
    };

    return (
        <PageCard title={kontaktinfoHovedlaantaker.title}>
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmitKontaktinformasjon)}>
                    <div className="row row-gap-large">
                        <span className="typography-ingress__light">{kontaktinfoHovedlaantaker.subTitle}</span>
                        <Input
                            inputMode="numeric"
                            label={kontaktinfoHovedlaantaker.labelMobil}
                            type="tel"
                            className="max-width-200"
                            error={errors.hovedlaantaker?.tlf?.message}
                            {...register('hovedlaantaker.tlf', {
                                required: `${validationMessage.requiredValue}`,
                                minLength: { value: 8, message: `${validationMessage.patternTlf}` },
                                maxLength: { value: 8, message: `${validationMessage.patternTlf}` },
                            })}
                        />
                        <Input
                            inputMode="email"
                            label={kontaktinfoHovedlaantaker.labelEpost}
                            className="max-width-400"
                            error={errors.hovedlaantaker?.epost?.message}
                            {...register('hovedlaantaker.epost', {
                                required: `${validationMessage.requiredValue}`,
                                pattern: {
                                    value: gyldigEpostFormat,
                                    message: `${validationMessage.patternEpost}`,
                                },
                            })}
                        />
                        {kontaktinformasjon?.visValgHarMedlaantaker && <MedlaantakerValg />}
                        {kontaktinformasjon?.visValgHarMedlaantaker && visMedlaantaker === ValgtWeb.JA && (
                            <MedlaantakerNew />
                        )}
                        {!kontaktinformasjon?.visValgHarMedlaantaker && medlaantaker && (
                            <>
                                <h2>{kontaktinfoMedlaantaker.title}</h2>
                                <InfoMessage
                                    text={kontaktinfoMedlaantaker.infoEksisterendeMedlaantaker}
                                    icon={IconTypes.INFO_36}
                                    type="default"
                                />
                                <span>{`${kontaktinfoMedlaantaker.navnEksisterendeMedlaantaker}: ${medlaantaker.fornavn} ${medlaantaker.etternavn}`}</span>
                                <MedlaantakerKontaktinfo />
                            </>
                        )}
                        <PageFooter
                            soeknadId={kontaktinformasjon?.soeknadId}
                            onForrigeClick={onForrigeClick}
                            showSpinner={isPending}
                        />
                    </div>
                </form>
            </FormProvider>
        </PageCard>
    );
};

export default Kontaktinformasjon;
